import React from 'react';
import { CheckCircle2, Circle, Clock, AlertTriangle } from 'lucide-react';
import { Task } from '../../types';

interface DueDiligenceListProps {
  tasks: Task[];
  categories: Array<{ id: string; name: string; color: string }>;
  onStatusChange: (taskId: string) => void;
}

export default function DueDiligenceList({ tasks, categories, onStatusChange }: DueDiligenceListProps) {
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 className="w-5 h-5 text-green-500" />;
      case 'in_progress':
        return <Clock className="w-5 h-5 text-yellow-500" />;
      default:
        return <Circle className="w-5 h-5 text-gray-300" />;
    }
  };

  const getCategoryColor = (categoryId: string) => {
    const category = categories.find(c => c.id === categoryId);
    return category?.color || 'gray';
  };

  const getPriorityIcon = (priority: string) => {
    switch (priority) {
      case 'high':
        return <AlertTriangle className="w-4 h-4 text-red-500" />;
      case 'medium':
        return <AlertTriangle className="w-4 h-4 text-yellow-500" />;
      default:
        return <AlertTriangle className="w-4 h-4 text-gray-400" />;
    }
  };

  return (
    <div className="divide-y divide-gray-200">
      {tasks.map((task) => (
        <div
          key={task.id}
          className="px-6 py-4 flex items-center justify-between hover:bg-gray-50 cursor-pointer"
          onClick={() => onStatusChange(task.id)}
        >
          <div className="flex items-center space-x-4">
            {getStatusIcon(task.status)}
            <div>
              <div className="flex items-center space-x-2">
                <span className="text-sm font-medium text-gray-900">{task.title}</span>
                {getPriorityIcon(task.priority)}
              </div>
              <div className="flex items-center mt-1 space-x-2">
                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-${getCategoryColor(task.category!)}-100 text-${getCategoryColor(task.category!)}-800`}>
                  {categories.find(c => c.id === task.category)?.name}
                </span>
                <span className="text-xs text-gray-500">
                  Due {new Date(task.dueDate).toLocaleDateString()}
                </span>
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-4">
            {task.assignee && (
              <div className="flex items-center">
                <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center">
                  <span className="text-xs font-medium text-gray-600">
                    {task.assignee[0].toUpperCase()}
                  </span>
                </div>
              </div>
            )}
            <div className="w-32 h-2 bg-gray-200 rounded-full">
              <div
                className="h-2 bg-blue-600 rounded-full"
                style={{ width: `${task.progress || 0}%` }}
              />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}